import { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import MainLayout from '../../components/Layout/MainLayout';
import VerificationCodeInput from '../../components/VerificationCodeInput';
import { useAuth } from '../../context/AuthContext';

/**
 * Login - Email + passcode login.
 * Step 1: enter email -> we email a short-lived passcode.
 * Step 2: enter the passcode -> session is established.
 */
function Login() {
  const { isLoggedIn, requestLoginCode, verifyLoginCode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState('email'); // 'email' | 'code'
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [codeResetKey, setCodeResetKey] = useState(0);

  // If already logged in, redirect back to wherever ProtectedRoute originally
  // bounced the user from (preserving query/hash too), or /scrumboard by default.
  useEffect(() => {
    if (isLoggedIn) {
      const from = location.state?.from;
      const dest = from ? from.pathname + (from.search || '') + (from.hash || '') : '/scrumboard';
      navigate(dest);
    }
  }, [isLoggedIn, navigate, location.state]);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || email.indexOf('@') === -1) {
      setError('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      await requestLoginCode(email);
      setStep('code');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyCode = async (fullCode) => {
    setError('');
    setSubmitting(true);
    try {
      await verifyLoginCode(email, fullCode);
      // On success the isLoggedIn effect above redirects to /scrumboard.
    } catch (err) {
      // Prefer the server's message (e.g. account-locked notice); fall back to a generic one.
      const serverMessage = err?.data?.error;
      setError(serverMessage || 'That code is invalid or has expired. Please try again.');
      setCodeResetKey((k) => k + 1); // clear the boxes so the user can re-enter
    } finally {
      setSubmitting(false);
    }
  };

  const handleUseDifferentEmail = () => {
    setStep('email');
    setError('');
    setCodeResetKey(0);
  };

  return (
    <MainLayout className="login-page">
      <div>
        <h3>Login</h3>
        <div className="well">
          {step === 'email' && (
            <form onSubmit={handleRequestCode}>
              <div className="row">
                <div className="col-md-12">
                  <h4 className="text-center">Sign in with your email</h4>
                  <p className="text-center">
                    Enter your email address and we&apos;ll send you a one-time code to sign in. No password needed.
                  </p>
                </div>
              </div>
              <div className="row" style={{ padding: '20px 0' }}>
                <div className="col-md-6 col-md-offset-3">
                  <div className="form-group">
                    <label htmlFor="login-email">Email address</label>
                    <input
                      id="login-email"
                      type="email"
                      className="form-control"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoFocus
                    />
                  </div>
                  {error && <p className="text-danger">{error}</p>}
                  <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
                    {submitting ? 'Sending…' : 'Send me a code'}
                  </button>
                </div>
              </div>
            </form>
          )}

          {step === 'code' && (
            <div>
              <div className="row">
                <div className="col-md-12">
                  <h4 className="text-center">Enter your code</h4>
                  <p className="text-center">
                    If an account exists for <strong>{email}</strong>, we&apos;ve sent a 6-digit code. It&apos;s valid for
                    10 minutes.
                  </p>
                </div>
              </div>
              <div className="row" style={{ padding: '20px 0' }}>
                <div className="col-md-6 col-md-offset-3">
                  <VerificationCodeInput
                    key={codeResetKey}
                    onComplete={handleVerifyCode}
                    disabled={submitting}
                    error={error}
                  />
                  {submitting && <p className="text-center text-muted" style={{ marginTop: '8px' }}>Verifying…</p>}
                  <button
                    type="button"
                    className="btn btn-link btn-block"
                    onClick={handleUseDifferentEmail}
                    disabled={submitting}
                  >
                    Use a different email
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="row">
            <div className="col-md-12">
              <p className="text-center">
                Don&apos;t have an account? <Link to="/signup">Request access</Link>. Questions?{' '}
                <Link to="/contact">Contact us</Link>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export default Login;
